import React from 'react';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';

/**
 * Service registry for unavailable service alerts.
 * Add new services here to support them in ServiceUnavailableAlert.
 */
export const SERVICE_REGISTRY = {
  claims: {
    singular: 'claim',
    plural: 'claims',
  },
  appeals: {
    singular: 'appeal',
    plural: 'appeals',
  },
};

export const ITEMS_PER_PAGE = 10;

export const FILTER_VALUES = {
  IN_PROGRESS: 'in-progress',
  CLOSED: 'closed',
  ALL: 'all',
};

// Date Formats
export const DATE_FORMATS = {
  LONG_DATE: 'MMMM d, yyyy',
};

export const CST_HOME_H1 =
  'Check your claim, decision review, or appeal status';

const CST_HOME_CRUMB = {
  href: '/your-claims',
  label: CST_HOME_H1,
  isRouterLink: true,
};

export const CST_BREADCRUMB_BASE = [
  { href: '/', label: 'VA.gov home' },
  CST_HOME_CRUMB,
];

// Matches the trail My VA builds from its side nav, where Claims and appeals
// status sits under Benefits.
export const CST_MY_VA_BREADCRUMB_BASE = [
  { href: '/my-va', label: 'My VA' },
  { href: '/my-va/benefits', label: 'Benefits' },
  CST_HOME_CRUMB,
];

// This is used in useDataDogRum
export const isProductionEnv = () => {
  return (
    !environment.BASE_URL.includes('localhost') &&
    !window.DD_RUM?.getInitConfiguration() &&
    !window.Mocha
  );
};

// The Standard 5103 Notice Response doesn't come through as a tracked item from our API until it is closed so we need to make a
// mocked item with information.
export const standard5103Item = {
  displayName: 'Review evidence list (5103 notice)',
  type: '5103 Notice Response',
  description: (
    <>
      <p>
        We sent you a “List of evidence we may need (5103 notice)” letter. This
        letter lets you know if submitting additional evidence will help decide
        your claim.
      </p>
      <p>
        You can also let us know that you’re done submitting additional
        evidence, for now.
      </p>
    </>
  ),
};

// File upload form text constants
export const LABEL_TEXT = 'Upload additional evidence';

export const CHAMPVA_HINT_TEXT =
  'You can upload a .pdf, .jpg, .jpeg, .png file. Your file should be no larger than 50 MB (non-PDF) or 99 MB (PDF only).';
export const VALIDATION_ERROR = 'Please select a file first';
export const PASSWORD_ERROR = 'Please provide a password to decrypt this file';
export const DOC_TYPE_ERROR = 'Please provide a document type';
export const SUBMIT_TEXT = 'Submit files for review';
export const SEND_YOUR_DOCUMENTS_TEXT = 'Send your documents another way';
export const DUPLICATE_FILE_ERROR = 'You already uploaded this file';

// Contact and submission information constants
export const MAILING_ADDRESS = {
  organization: 'Department of Veterans Affairs',
  department: 'Evidence Intake Center',
  poBox: 'PO Box 4444',
  city: 'Janesville',
  state: 'WI',
  zip: '53547-4444',
};

export const CONTACT_INFO = {
  phone: '800-827-1000',
  tty: '711',
  hours: 'Monday through Friday, 8:00 a.m. to 9:00 p.m. ET',
};

export const ANCHOR_LINKS = {
  addFiles: 'add-files',
  otherWaysToSendDocuments: 'other-ways-to-send',
  fileSubmissionsInProgress: 'file-submissions-in-progress',
  filesReceived: 'files-received',
  filesWeCouldntReceive: 'files-we-couldnt-receive',
};

export const INTENT_TO_FILE_PATH = 'your-claims/your-intents-to-file';

export const VRE_TRACKER_PATH = '/careers-employment/track-your-vre-benefits/';

// Appeal detail tabs, in display order. The `id` is the canonical va-tabs
// value (?tab=<id>). The legacy flag-off route path stays /detail (see
// routes.jsx) until the flag is removed (#144227).
export const APPEAL_TABS = [
  { id: 'status', label: 'Status' },
  { id: 'issues', label: 'Issues' },
];

// The tab to show when the URL has no ?tab= value.
export const DEFAULT_APPEAL_TAB = APPEAL_TABS[0].id;

// Claim detail tabs, in display order. The `id` is the canonical va-tabs value
// (?tab=<id>) and matches the legacy flag-off route path.
export const CLAIM_TABS = [
  { id: 'status', label: 'Status' },
  { id: 'files', label: 'Files' },
  { id: 'overview', label: 'Overview' },
];

export const DEFAULT_CLAIM_TAB = CLAIM_TABS[0].id;

export const LINKS = {
  findVaLocations: '/find-locations',
  disabilityCompensationClaimIntro:
    '/disability/file-disability-claim-form-21-526ez',
  supplementalClaimIntro:
    '/decision-reviews/supplemental-claim/file-supplemental-claim-form-20-0995',
  veteransPensionOnlineIntro:
    '/pension/apply-for-veteran-pension-form-21p-527ez',
  intentToFileForm0966: '/forms/21-0966/submit-intent-to-file',
  intentToFileAboutClaim: '/resources/your-intent-to-file-a-va-claim/',
};

export const TRACKED_ITEM_STATUSES = {
  ACCEPTED: 'ACCEPTED',
  INITIAL_REVIEW_COMPLETE: 'INITIAL_REVIEW_COMPLETE',
  NEEDED_FROM_OTHERS: 'NEEDED_FROM_OTHERS',
  NEEDED_FROM_YOU: 'NEEDED_FROM_YOU',
  NO_LONGER_REQUIRED: 'NO_LONGER_REQUIRED',
  SUBMITTED_AWAITING_REVIEW: 'SUBMITTED_AWAITING_REVIEW',
};

export const QUICK_SUBMIT_URL =
  'https://login.va.gov/app/va_quicksubmit2667_1/exk1q9erdmmzB5S9f0j7/sso/saml';

export const FILE_UPLOAD_GUIDANCE = (
  <p className="vads-u-margin-top--1 vads-u-margin-bottom--3 va-introtext">
    Add new evidence or review files you’ve already uploaded for this claim.
    Only submit evidence supporting this claim here.
  </p>
);

export const QUICK_SUBMIT_GUIDANCE = (
  <>
    <h3 className="vads-u-margin-top--0 vads-u-margin-bottom--1">
      If you need to upload files that aren’t evidence
    </h3>
    <p className="vads-u-margin-top--0">
      Use QuickSubmit to upload new claims, decision review requests, or other
      procedural documents like requests to switch Board Appeal types or Freedom
      of Information Act (FOIA) requests. If you upload those here, they may not
      be routed to the proper office to be processed.
    </p>
    <va-link
      class="vads-u-display--block vads-u-margin-bottom--3"
      external
      href={QUICK_SUBMIT_URL}
      text="Upload files using QuickSubmit"
    />
  </>
);

export const TRACKED_ITEMS_4142 = [
  '21-4142a',
  'VA Form 21 4142 Not HIPAA Compliant',
  '21-4142',
  '21-4142/21-4142a',
  '21-4142 incomplete - need provider address',
  `VAF 21-4142 for vet's treatment records/autopsy for death c`,
];
